import chalk from 'chalk';
import { loadConfig, getDefaultConfig } from '../../engine/config-loader.js';
import { resolveRules, groupRulesByTool, calculateScore, shouldFail } from '../../engine/rule-engine.js';
import { loadBaseline, filterNewIssues, getBaselineStats } from '../../engine/baseline.js';
import { getChangedFiles, filterResultsByFiles } from '../../engine/diff-mode.js';
import { gitleaksRunner, semgrepRunner, osvScannerRunner, runBuiltinChecks } from '../../tools/index.js';
import type { CheckResult, CheckReport, RuleSeverity } from '../../types/index.js';

interface CIOptions {
  failOn?: string;
  format?: string;
  newOnly?: boolean;
  diffOnly?: boolean;
}

interface CIReport extends CheckReport {
  baseline?: {
    total: number;
    new: number;
    baselined: number;
    fixed: number;
  };
  newIssues: CheckResult[];
  diffOnly: boolean;
  changedFiles: number;
}

export async function ciCommand(options: CIOptions): Promise<void> {
  const cwd = process.cwd();
  const startTime = Date.now();
  const failOn = (options.failOn ?? 'error') as RuleSeverity;
  const format = options.format ?? 'console';
  const useBaseline = options.newOnly ?? true; // Default: only fail on NEW issues
  const useDiffOnly = options.diffOnly ?? false;

  try {
    const loaded = await loadConfig(cwd);
    const config = loaded?.config ?? getDefaultConfig();

    // Load baseline for PR gate
    const baseline = useBaseline ? await loadBaseline(cwd) : null;

    // Get changed files for diff-only mode
    const changedFiles = useDiffOnly ? await getChangedFiles(cwd) : [];

    const resolvedRules = resolveRules(config);
    const rulesByTool = groupRulesByTool(resolvedRules);

    const allResults: CheckResult[] = [];
    const toolStats: CheckReport['tools'] = [];

    const builtinRules = rulesByTool.get('builtin') ?? [];
    if (builtinRules.length > 0) {
      const builtinStart = Date.now();
      const builtinResults = await runBuiltinChecks(
        cwd,
        builtinRules.map((r) => r.definition.id)
      );
      allResults.push(...builtinResults);
      toolStats.push({
        name: 'builtin',
        duration: Date.now() - builtinStart,
        results: builtinResults.length,
      });
    }

    if (config.tools?.gitleaks !== false) {
      const isInstalled = await gitleaksRunner.isInstalled();
      if (isInstalled) {
        const toolStart = Date.now();
        const results = await gitleaksRunner.run(cwd);
        allResults.push(...results);
        toolStats.push({
          name: 'gitleaks',
          version: (await gitleaksRunner.getVersion()) ?? undefined,
          duration: Date.now() - toolStart,
          results: results.length,
        });
      }
    }

    if (config.tools?.osvScanner !== false) {
      const isInstalled = await osvScannerRunner.isInstalled();
      if (isInstalled) {
        const toolStart = Date.now();
        const results = await osvScannerRunner.run(cwd);
        allResults.push(...results);
        toolStats.push({
          name: 'osv-scanner',
          version: (await osvScannerRunner.getVersion()) ?? undefined,
          duration: Date.now() - toolStart,
          results: results.length,
        });
      }
    }

    if (config.tools?.semgrep !== false) {
      const isInstalled = await semgrepRunner.isInstalled();
      if (isInstalled) {
        const toolStart = Date.now();
        const results = await semgrepRunner.run(cwd);
        allResults.push(...results);
        toolStats.push({
          name: 'semgrep',
          version: (await semgrepRunner.getVersion()) ?? undefined,
          duration: Date.now() - toolStart,
          results: results.length,
        });
      }
    }

    // Apply diff-only filter if enabled
    const filteredResults: CheckResult[] = useDiffOnly && changedFiles.length > 0
      ? filterResultsByFiles(allResults, changedFiles) as CheckResult[]
      : allResults;

    // Apply baseline filter - only NEW issues will cause failure
    const newIssues = filterNewIssues(filteredResults, baseline);
    const baselineStats = getBaselineStats(allResults, baseline);

    const duration = Date.now() - startTime;
    const score = calculateScore(allResults); // Score on ALL issues

    const report: CIReport = {
      timestamp: new Date().toISOString(),
      duration,
      preset: config.preset,
      level: config.level ?? 'standard',
      summary: {
        total: allResults.length,
        errors: allResults.filter((r) => r.severity === 'error').length,
        warnings: allResults.filter((r) => r.severity === 'warn').length,
        infos: allResults.filter((r) => r.severity === 'info').length,
        fixed: 0,
      },
      score,
      results: allResults,
      tools: toolStats,
      // PR Gate stats
      baseline: baseline ? {
        total: baselineStats.total,
        new: baselineStats.new,
        baselined: baselineStats.baselined,
        fixed: baselineStats.fixed,
      } : undefined,
      newIssues,
      diffOnly: useDiffOnly,
      changedFiles: changedFiles.length,
    };

    if (format === 'json') {
      console.log(JSON.stringify(report, null, 2));
    } else if (format === 'sarif') {
      console.log(JSON.stringify(toSarif(report), null, 2));
    } else {
      printCIReport(report, useBaseline);
    }

    // PR Gate: Only fail on NEW issues (not baselined)
    const issuesToCheck = useBaseline ? newIssues : allResults;
    if (shouldFail(issuesToCheck, failOn)) {
      if (useBaseline && newIssues.length > 0) {
        console.log(chalk.red(`\n❌ CI failed: ${newIssues.length} NEW issue(s) detected\n`));
      }
      process.exit(1);
    }

    if (useBaseline && baseline) {
      console.log(chalk.green(`\n✓ CI passed (${baselineStats.baselined} baselined issues ignored)\n`));
    }
  } catch (error) {
    console.error(chalk.red('CI check failed:'), error);
    process.exit(1);
  }
}

function printCIReport(report: CIReport, useBaseline: boolean): void {
  console.log(chalk.bold('\n🛤️  Devrail CI Report\n'));

  const scoreColor = report.score >= 80 ? chalk.green : report.score >= 50 ? chalk.yellow : chalk.red;
  console.log(`Score: ${scoreColor.bold(report.score + '/100')}`);

  // Show baseline stats if available
  if (useBaseline && report.baseline) {
    console.log(chalk.bold('\n📊 PR Gate Status:'));
    console.log(`  Total issues:     ${report.baseline.total}`);
    console.log(`  ${chalk.green('Fixed:')}           ${report.baseline.fixed}`);
    console.log(`  ${chalk.dim('Baselined:')}       ${report.baseline.baselined}`);
    console.log(`  ${chalk.red('NEW:')}             ${report.baseline.new}`);
  }

  console.log(`\nAll Findings:`);
  console.log(`  ${chalk.red('Errors:')} ${report.summary.errors}`);
  console.log(`  ${chalk.yellow('Warnings:')} ${report.summary.warnings}`);
  console.log(`  ${chalk.blue('Info:')} ${report.summary.infos}`);

  // Show NEW issues prominently
  if (report.newIssues.length > 0) {
    console.log('\n' + chalk.bold.red('🚨 NEW Issues (will fail CI):'));
    for (const result of report.newIssues) {
      const icon =
        result.severity === 'error'
          ? chalk.red('✖')
          : result.severity === 'warn'
            ? chalk.yellow('⚠')
            : chalk.blue('ℹ');
      const location = result.file ? `${result.file}${result.line ? `:${result.line}` : ''}` : '';
      console.log(`  ${icon} [${result.ruleId}] ${result.message}`);
      if (location) console.log(`    ${chalk.dim(location)}`);
    }
  } else if (useBaseline && report.baseline && report.baseline.baselined > 0) {
    console.log('\n' + chalk.green('✓ No NEW issues! Baselined issues are ignored.'));
  }

  if (report.diffOnly) {
    console.log(chalk.dim(`\nMode: diff-only (${report.changedFiles} files changed)`));
  }

  console.log(`\n${chalk.dim(`Duration: ${report.duration}ms | Tools: ${report.tools.map((t) => t.name).join(', ')}`)}`);
}

function toSarif(report: CheckReport): object {
  return {
    $schema: 'https://raw.githubusercontent.com/oasis-tcs/sarif-spec/master/Schemata/sarif-schema-2.1.0.json',
    version: '2.1.0',
    runs: [
      {
        tool: {
          driver: {
            name: 'VibeGuard',
            version: '0.1.0',
            informationUri: 'https://vibeguard.dev',
            rules: report.results.map((r) => ({
              id: r.ruleId,
              shortDescription: { text: r.message },
            })),
          },
        },
        results: report.results.map((r) => ({
          ruleId: r.ruleId,
          level: r.severity === 'error' ? 'error' : r.severity === 'warn' ? 'warning' : 'note',
          message: { text: r.message },
          locations: r.file
            ? [
                {
                  physicalLocation: {
                    artifactLocation: { uri: r.file },
                    region: r.line
                      ? {
                          startLine: r.line,
                          startColumn: r.column ?? 1,
                          endLine: r.endLine ?? r.line,
                          endColumn: r.endColumn ?? r.column ?? 1,
                        }
                      : undefined,
                  },
                },
              ]
            : [],
        })),
      },
    ],
  };
}
